import { useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { LineChart } from "../../lib/charts";
import { addDays, iso, monthEnd, monthStart } from "../../lib/dates";
import { Notice, Stat } from "../../lib/ui";

/* Monthly sales & profit – port of salesExtend.php (API: /api/sales/monthly). This month, last month, last 12 months or a custom range. */

export default function SalesMonthly() {
  const api = useApi();
  const [filter, setFilter] = useState("this_month");
  const [custom, setCustom] = useState({ start: iso(monthStart(new Date(new Date().getFullYear(), new Date().getMonth() - 5, 1))), end: iso(monthEnd(new Date())) });
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const range = useMemo(() => {
    const t = new Date(), lm = addDays(monthStart(t), -1);
    if (filter === "this_month") return { start: iso(monthStart(t)), end: iso(monthEnd(t)) };
    if (filter === "last_month") return { start: iso(monthStart(lm)), end: iso(monthEnd(lm)) };
    if (filter === "custom") return custom;
    return { start: iso(new Date(t.getFullYear(), t.getMonth() - 11, 1)), end: iso(monthEnd(t)) };
  }, [filter, custom]);
  useEffect(() => { api.get("/api/sales/monthly", range).then(setD).catch((e) => setError(e.message)); }, [api, range]);

  return (
    <main className="pg">
      <PageHeader slug="salesExtend" />
      <Notice notice={error && { tone: "err", text: error }} />
      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <div className="presets" style={{ paddingBottom: ".8rem", alignItems: "center" }}>
          {[["this_month", "This month"], ["last_month", "Last month"], ["last_12", "Last 12 months"], ["custom", "Custom"]].map(([k, l]) =>
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>)}
          {filter === "custom" && <>
            <input type="date" aria-label="From" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} />
            <input type="date" aria-label="To" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} /></>}
          <span style={{ marginLeft: "auto", fontSize: ".8rem", color: "var(--muted)" }}>{range.start} to {range.end}</span>
        </div>
      </section>
      {d && (
        <>
          <div className="ui-stats">
            <Stat icon="fa-coins" tone="up" label="Total sales" value={n0(d.total_sales)} unit="TZS" />
            <Stat icon="fa-sack-dollar" label="Total profit" value={n0(d.total_profit)} unit="TZS" />
            <Stat icon="fa-percent" tone="warn" label="Profit margin" value={`${d.profit_margin}%`} />
            <Stat icon="fa-receipt" tone="muted" label="Sale lines" value={n0(d.transactions)} />
          </div>
          <section className="panel card-pad" style={{ marginBottom: "1.25rem" }}>
            <h3>Sales and profit by month</h3>
            <LineChart labels={d.months.map((m) => m.month)} series={[{ name: "Sales", values: d.months.map((m) => m.total_sales) }, { name: "Profit", values: d.months.map((m) => m.total_profit) }]} />
          </section>
          <section className="panel"><div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Month</th><th className="num">Sales</th><th className="num">Profit</th><th className="num">Margin</th></tr></thead>
            <tbody>{d.months.map((m) => <tr key={m.month}><td>{m.month}</td><td className="num mono">{n2(m.total_sales)}</td><td className="num mono">{n2(m.total_profit)}</td>
              <td className="num mono">{m.total_sales > 0 ? ((m.total_profit / m.total_sales) * 100).toFixed(1) : "0"}%</td></tr>)}</tbody>
          </table>{d.months.length === 0 && <div className="empty">No sales in this range.</div>}</div></section>
        </>
      )}
    </main>
  );
}
